import { authFetch, isDelegated, type AuthSession } from '../auth';

async function activeWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorker> {
  if (registration.active?.state === 'activated') return registration.active;
  const worker = registration.installing ?? registration.waiting ?? registration.active;
  if (!worker) throw new Error('Download konnte nicht vorbereitet werden.');
  return new Promise((resolve,reject) => {
    const timer = setTimeout(() => { worker.removeEventListener('statechange', changed); reject(new Error('Download konnte nicht vorbereitet werden.')); }, 10000);
    const changed = () => {
      if (worker.state === 'activated') { clearTimeout(timer); worker.removeEventListener('statechange',changed); resolve(worker); }
      if (worker.state === 'redundant') { clearTimeout(timer); worker.removeEventListener('statechange',changed); reject(new Error('Download konnte nicht vorbereitet werden.')); }
    };
    worker.addEventListener('statechange',changed); changed();
  });
}

/** Preserve normal auth/refresh handling, then bridge a bounded response stream
 * to the browser's native download. Neither auth nor file contents enter a URL,
 * localStorage, CacheStorage or a Blob containing the whole archive. */
export type ExportFileName='immobilien-unterlagen.tar'|'immobilien-konto-haushalt.tar';
export async function downloadArchive(user:AuthSession,url:string,fileName:ExportFileName,onStarted:()=>void,signal?:AbortSignal):Promise<void>{
  if (isDelegated(user)) throw new Error('Der Datenexport benötigt die normale Haushaltsanmeldung.');
  if (!('serviceWorker' in navigator) || !window.isSecureContext) throw new Error('Der Datenexport benötigt einen aktuellen Browser und eine sichere Verbindung.');
  const abort=new AbortController();
  const externalAbort=()=>abort.abort();
  signal?.addEventListener('abort',externalAbort,{once:true});
  if(signal?.aborted)abort.abort();
  try {
  const registration = await navigator.serviceWorker.register('/evidence-download/worker.js', { scope: '/evidence-download/' });
  const worker = await activeWorker(registration);
  if(abort.signal.aborted)throw new Error('Datenexport abgebrochen.');
  const response: Response = await authFetch(user, url,
    { signal: abort.signal, redirect: 'error', credentials: 'same-origin', cache: 'no-store' }, 'response');
  if (!response.body || response.headers.get('Content-Type')?.split(';')[0] !== 'application/x-tar') {
    abort.abort(); throw new Error('Datenexport nicht verfügbar.');
  }
  const reader = response.body.getReader(), channel = new MessageChannel();
  const frame = document.createElement('iframe'); frame.hidden = true; frame.title = 'Datenexport';
  const path = `/evidence-download/${crypto.randomUUID()}.tar`;
  await new Promise<void>((resolve,reject) => {
    let timeout: ReturnType<typeof setTimeout>, reading = false, started = false, ended = false;
    let buffered: Uint8Array | undefined, offset = 0;
    const cleanup = () => { abort.signal.removeEventListener('abort',fail); clearTimeout(timeout); window.removeEventListener('pagehide',leave); channel.port1.close(); frame.remove(); abort.abort(); void reader.cancel().catch(()=>{}); };
    const fail = () => { if (ended) return; ended = true; channel.port1.postMessage({type:'error'}); cleanup(); reject(new Error('Der Datenexport wurde unterbrochen. Bitte den Download erneut starten.')); };
    const leave = () => fail();
    const deadline = () => { clearTimeout(timeout); timeout = setTimeout(fail,35000); };
    window.addEventListener('pagehide',leave); deadline();
    channel.port1.onmessageerror = fail;
    channel.port1.onmessage = async ({data}) => {
      deadline();
      if (data?.type === 'ready' && !started) { started = true; frame.src = path; document.body.append(frame); onStarted(); }
      else if (data?.type === 'pull' && started && !reading && !ended) {
        reading = true;
        try {
          if (!buffered) {
            const item = await reader.read();
            if (ended) return;
            if (item.done) { channel.port1.postMessage({type:'end'}); return; }
            buffered = item.value; offset = 0;
          }
          const end = Math.min(buffered.byteLength, offset + 8 * 1024 * 1024);
          const bytes = buffered.slice(offset,end).buffer; offset = end;
          if (offset === buffered.byteLength) buffered = undefined;
          channel.port1.postMessage({type:'chunk',bytes},[bytes]);
        } catch { fail(); } finally { reading = false; }
      } else if (data?.type === 'finished' && started && !ended) { ended = true; cleanup(); resolve(); }
      else fail();
    };
    abort.signal.addEventListener('abort',fail,{once:true});
    if(abort.signal.aborted){fail();return;}
    try { worker.postMessage({path,filename:fileName},[channel.port2]); } catch { fail(); }
  });
  } finally { signal?.removeEventListener('abort',externalAbort); }
}

export async function exportEvidence(user:AuthSession,listingId:string,version:number,onStarted:()=>void):Promise<void>{
  return downloadArchive(user,`/api/research/properties/${encodeURIComponent(listingId)}/due-diligence/export?version=${version}`,'immobilien-unterlagen.tar',onStarted);
}
